import { type BadgeColor } from '@precis-digital/kurama/Badge'
import { type ChipVariant } from '@precis-digital/kurama/Chip'
import { type AssetStatus } from './types'
import { type ResourceType } from 'shared/api/tags'

export const PROJECT_PAGE_TABS = ['overview', 'ad-library', 'brand-portal']
export const DEFAULT_PROJECT_PAGE_TAB = 'overview'

export const AD_LIBRARY_FILTER_SEARCH_MIN_OPTIONS = 5

export const ASSET_STATUSES = {
  approved: 'approved',
  changes_requested: 'changes_requested',
  needs_review: 'needs_review',
  archived: 'archived',
  internal: 'internal'
} as const

export const ASSET_STATUS_VALUES = {
  approved: 'APPROVED',
  changes_requested: 'NEEDS AMENDS',
  needs_review: 'READY FOR REVIEW',
  archived: 'ARCHIVED',
  internal: 'INTERNAL'
} as const

export type StatusValues = (typeof ASSET_STATUS_VALUES)[keyof typeof ASSET_STATUS_VALUES]

export const ASSET_STATUS_METADATA: Record<
  AssetStatus,
  { label: string; chipVariant: ChipVariant; badgeColor: BadgeColor; value: StatusValues }
> = {
  [ASSET_STATUSES.approved]: {
    label: 'Approved',
    chipVariant: 'semantic-success',
    badgeColor: 'success',
    value: ASSET_STATUS_VALUES.approved
  },
  [ASSET_STATUSES.changes_requested]: {
    label: 'Changes requested',
    chipVariant: 'semantic-warning',
    badgeColor: 'warning',
    value: ASSET_STATUS_VALUES.changes_requested
  },
  [ASSET_STATUSES.needs_review]: {
    label: 'Awaiting review',
    chipVariant: 'semantic-destructive',
    badgeColor: 'error',
    value: ASSET_STATUS_VALUES.needs_review
  },
  [ASSET_STATUSES.archived]: {
    label: 'Archived',
    chipVariant: 'tonal',
    badgeColor: 'stone',
    value: ASSET_STATUS_VALUES.archived
  },
  [ASSET_STATUSES.internal]: {
    label: 'Internal',
    chipVariant: 'semantic-info',
    badgeColor: 'info',
    value: ASSET_STATUS_VALUES.internal
  }
}

export const DEFAULT_PROJECT_NAMING_CONVENTION = 'Brand_Project-Name_Language_Channel_Dimensions_Variant_Placement-Type'

export const VIEW_STATE_TYPES = {
  tabs: 'tabs',
  requestChangeDetails: 'requestChangeDetails',
  requestChangesForm: 'requestChangesForm'
} as const

export const REQUESTED_CHANGE_PRIORITY_OPTIONS = {
  high: 'high',
  low: 'low'
}

export const REQUESTED_CHANGE_STATUS_OPTIONS = {
  open: 'open',
  resolved: 'resolved'
} as const

export const MENTION_SYNTAX_REGEX = /@\[user:([^\]]+)\]/g
export const MENTION_HREF_PREFIX = '#mention-'
export const REQUEST_CHANGE_LINK_QUERY_PARAM = 'changeId'

export const HTML_ENTRY_FILES = {
  index: 'index.html',
  combinedPlacement: 'combined_placement.html',
  responsive: 'responsive.html'
} as const

export const HTML_ENTRY_FILE_NAMES: readonly string[] = Object.values(HTML_ENTRY_FILES)

export const HTML_ENTRY_TYPES = {
  responsive: 'responsive',
  combinedPlacement: 'combined_placement'
} as const

export type HtmlEntryType = (typeof HTML_ENTRY_TYPES)[keyof typeof HTML_ENTRY_TYPES]

export const HTML_ENTRY_TYPE_TRANSLATION_KEY = {
  [HTML_ENTRY_TYPES.responsive]: 'responsive',
  [HTML_ENTRY_TYPES.combinedPlacement]: 'combinedPlacement'
} as const satisfies Record<HtmlEntryType, 'responsive' | 'combinedPlacement'>

// Changes requested status will be updated via the request changes form
export const ALLOWED_STATUSES_UPDATE = [
  ASSET_STATUSES.internal,
  ASSET_STATUSES.needs_review,
  ASSET_STATUSES.approved,
  ASSET_STATUSES.archived
]

export const BULK_UPDATE_BATCH_SIZE = 20

const TAG_RESOURCE_TYPE: Record<ResourceType, ResourceType> = {
  creative_asset: 'creative_asset',
  creative_reporting: 'creative_reporting'
}

export const TAG_RESOURCE_TYPES: ResourceType[] = [
  TAG_RESOURCE_TYPE.creative_asset,
  TAG_RESOURCE_TYPE.creative_reporting
]
